import { LabelChips } from '../LabelChips.js';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { V2Badge } from '@sakhtar/design-system/react';
import { ApiError, apiPost } from '../../../lib/apiClient.js';
import { useApiGet } from '../../../lib/useApiGet.js';
import { PartyLink } from '../PartyLink.js';
import { JalaliDateTimeInput } from '../../../lib/date/JalaliDateTimeInput.js';
import {
  dueTone,
  exactWhen,
  relativeWhen,
  wallClockToIso,
} from '../../../lib/date/relativeWhen.js';
import { faDigits } from '../../../lib/faDigits.js';
import { displayJalaliDate } from '../../../lib/date/displayJalali.js';
import { formatRialFa } from '../../../lib/finance-display.js';
import { useAmountDisplay } from '../../../lib/tenantSettings.js';
import { SidePanel } from '../../../components/list/SidePanel.js';
import type { LeadContactReveal, LeadDetail } from '../../../lib/types/crm.js';
import type { MasterDataListResponse } from '../../../lib/types/masterData.js';
import {
  LEAD_PRIORITY_TONE,
  LEAD_STATUS_TONE,
  fieldErrorsFrom,
  formatMasked,
  initialsOf,
} from './leadUi.js';

type Mode = 'view' | 'stage' | 'win' | 'lose' | 'call';
/** What the panel opens into — row-menu actions land directly in their form. */
export type LeadPanelMode = Mode | 'edit';

const CALL_OUTCOMES = ['پاسخ داد', 'پاسخ نداد', 'نیازمند پیگیری', 'درخواست ارسال پیشنهاد'] as const;

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        padding: '10px 0',
        borderTop: '1px solid var(--sk-divider)',
        fontSize: 13,
      }}
    >
      <span style={{ color: 'var(--sk-fg-muted)', flex: '0 0 auto' }}>{label}</span>
      <div style={{ color: 'var(--sk-fg)', textAlign: 'start', minWidth: 0 }}>{children}</div>
    </div>
  );
}

/**
 * Lead detail (Sales CRM §02 LEAD DETAIL · 520px): identity, masked contact
 * with an audited «نمایش کامل», source, need, last activity / next action,
 * the deal (stage, amount, expected close, priority, products), the stage
 * history and timeline, and the real actions — move to a later stage, log a
 * call, «برنده شد» (the lead becomes a customer) and «از دست رفت» (with a
 * reason). There is no separate opportunity record any more.
 */
export function LeadDetailPanel({
  base,
  leadRef,
  initialMode = 'view',
  refreshKey,
  onClose,
  onEdit,
  onChanged,
}: {
  base: string;
  leadRef: string | null;
  initialMode?: LeadPanelMode;
  refreshKey: number;
  onClose: () => void;
  onEdit: (lead: LeadDetail) => void;
  onChanged: (message: string) => void;
}) {
  const [localRefresh, setLocalRefresh] = useState(0);
  const detail = useApiGet<LeadDetail>(leadRef ? `${base}/api/v1/crm/leads/${leadRef}` : null, [
    leadRef,
    refreshKey,
    localRefresh,
  ]);
  const [mode, setMode] = useState<Mode>('view');
  const [revealed, setRevealed] = useState<LeadContactReveal | null>(null);
  const [revealing, setRevealing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ReactNode>(null);

  const { unit, label: unitLabel } = useAmountDisplay();
  const [stageRef, setStageRef] = useState('');
  const [winChoice, setWinChoice] = useState<'default' | 'create' | 'existing'>('default');
  const [winPartyRef, setWinPartyRef] = useState('');
  const [lostReasonCode, setLostReasonCode] = useState('');
  const [lostNote, setLostNote] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [callSummary, setCallSummary] = useState('');
  const [callOutcome, setCallOutcome] = useState<string>(CALL_OUTCOMES[0]);
  const [callNext, setCallNext] = useState('');

  useEffect(() => {
    setMode(initialMode === 'edit' ? 'view' : initialMode);
    setRevealed(null);
    setError(null);
    setResult(null);
    setFieldErrors({});
    setStageRef('');
    setWinChoice('default');
    setWinPartyRef('');
    setLostReasonCode('');
    setLostNote('');
  }, [leadRef, initialMode]);

  const lead = detail.status === 'ready' ? detail.data : null;

  // Row-menu actions that need the loaded lead: prefill convert, hand edit over.
  const [consumedFor, setConsumedFor] = useState<string | null>(null);
  useEffect(() => {
    if (!lead || consumedFor === lead.ref) return;
    setConsumedFor(lead.ref);
    if (initialMode === 'stage') setStageRef(lead.allowedStages[0]?.ref ?? '');
    if (initialMode === 'edit' && lead.actions.canUpdate) onEdit(lead);
  }, [lead]);
  useEffect(() => setConsumedFor(null), [leadRef, initialMode]);
  const refresh = () => setLocalRefresh((k) => k + 1);

  // Only the customer picker needs the customer list, and only while winning.
  const customers = useApiGet<MasterDataListResponse>(
    mode === 'win' && winChoice === 'existing'
      ? `${base}/api/v1/master-data/parties?role=customer&limit=200`
      : null,
    [mode, winChoice],
  );

  async function reveal() {
    if (!lead) return;
    setRevealing(true);
    try {
      setRevealed(
        await apiPost<LeadContactReveal>(`${base}/api/v1/crm/leads/${lead.ref}/contact/reveal`, {}),
      );
    } catch {
      setError('نمایش شماره انجام نشد.');
    } finally {
      setRevealing(false);
    }
  }

  async function run(action: () => Promise<ReactNode>) {
    setBusy(true);
    setError(null);
    setFieldErrors({});
    try {
      const message = await action();
      setMode('view');
      setResult(message);
      refresh();
    } catch (err) {
      const fields = fieldErrorsFrom(err);
      setFieldErrors(fields);
      setError(
        Object.keys(fields).length > 0
          ? 'اطلاعات واردشده کامل نیست.'
          : err instanceof ApiError && err.status === 403
            ? 'دسترسی لازم برای این کار را ندارید.'
            : err instanceof ApiError && err.status === 409
              ? 'وضعیت این سرنخ در این بین تغییر کرده است؛ صفحه را تازه کنید.'
              : 'ثبت انجام نشد. دوباره تلاش کنید.',
      );
    } finally {
      setBusy(false);
    }
  }

  const moveStage = () =>
    run(async () => {
      if (!lead) return null;
      if (!stageRef) throw new Error('stage');
      await apiPost(`${base}/api/v1/crm/leads/${lead.ref}/stage`, { stageRef });
      const name = lead.allowedStages.find((st) => st.ref === stageRef)?.name ?? '';
      onChanged('مرحلهٔ سرنخ تغییر کرد.');
      return `سرنخ به مرحلهٔ «${name}» منتقل شد.`;
    });

  const win = () =>
    run(async () => {
      if (!lead) return null;
      const body = lead.deal.customer
        ? {}
        : winChoice === 'existing'
          ? { partyRef: winPartyRef }
          : { createCustomer: true };
      await apiPost(`${base}/api/v1/crm/leads/${lead.ref}/win`, body);
      onChanged('سرنخ برنده شد و به مشتری تبدیل شد.');
      return 'برنده شد — سرنخ به مشتری تبدیل شد.';
    });

  const lose = () =>
    run(async () => {
      if (!lead) return null;
      await apiPost(`${base}/api/v1/crm/leads/${lead.ref}/lose`, {
        lostReasonCode,
        note: lostNote.trim() || undefined,
      });
      setLostNote('');
      onChanged('سرنخ از دست رفت.');
      return 'سرنخ با دلیل ثبت‌شده از دست رفت.';
    });

  const logCall = () =>
    run(async () => {
      if (!lead) return null;
      const title = `تماس — ${callOutcome}${callSummary.trim() ? `: ${callSummary.trim()}` : ''}`;
      const created = await apiPost<{ ref: string }>(`${base}/api/v1/crm/activities`, {
        title,
        activityType: 'call',
        leadRef: lead.ref,
      });
      await apiPost(`${base}/api/v1/crm/activities/${created.ref}/complete`, {});
      const nextIso = callNext ? wallClockToIso(callNext) : null;
      if (nextIso) {
        await apiPost(`${base}/api/v1/crm/activities`, {
          title: `پیگیری ${lead.title}`,
          activityType: 'call',
          leadRef: lead.ref,
          dueAt: nextIso,
        });
      }
      setCallSummary('');
      setCallNext('');
      onChanged('تماس ثبت شد.');
      return nextIso ? 'تماس و پیگیری بعدی ثبت شد.' : 'تماس ثبت شد.';
    });

  const next =
    lead?.nextAction ??
    (lead?.deal.ownNextAction
      ? { title: lead.deal.ownNextAction.title, dueAt: lead.deal.ownNextAction.dueAt }
      : null);
  const tone = dueTone(next?.dueAt ?? null);

  return (
    <SidePanel
      open={leadRef !== null}
      title={lead ? lead.title : 'سرنخ'}
      onClose={onClose}
      dirty={mode !== 'view' && (callSummary !== '' || lostNote !== '')}
      footer={
        lead && mode === 'view' ? (
          <>
            {lead.actions.canWin ? (
              <button
                type="button"
                className="sk-btn sk-btn--primary"
                onClick={() => {
                  setError(null);
                  setMode('win');
                }}
              >
                برنده شد
              </button>
            ) : null}
            {lead.actions.canMoveStage && lead.allowedStages.length > 0 ? (
              <button
                type="button"
                className="sk-btn sk-btn--secondary"
                onClick={() => {
                  setError(null);
                  setStageRef(lead.allowedStages[0]?.ref ?? '');
                  setMode('stage');
                }}
              >
                انتقال مرحله
              </button>
            ) : null}
            {lead.actions.canLogActivity ? (
              <button
                type="button"
                className="sk-btn sk-btn--secondary"
                onClick={() => setMode('call')}
              >
                ثبت تماس
              </button>
            ) : null}
            {lead.actions.canQuote && lead.status !== 'disqualified' ? (
              <Link
                to={`${base}/crm/quotes?lead=${encodeURIComponent(lead.ref)}`}
                className="sk-btn sk-btn--secondary"
                style={{ textDecoration: 'none' }}
              >
                پیش‌فاکتور
              </Link>
            ) : null}
            {lead.actions.canUpdate ? (
              <button type="button" className="sk-btn sk-btn--ghost" onClick={() => onEdit(lead)}>
                ویرایش
              </button>
            ) : null}
            {lead.actions.canLose ? (
              <button
                type="button"
                className="sk-btn sk-btn--ghost"
                style={{ color: 'var(--sk-danger)' }}
                onClick={() => {
                  setError(null);
                  setMode('lose');
                }}
              >
                از دست رفت
              </button>
            ) : null}
          </>
        ) : undefined
      }
    >
      {detail.status === 'loading' ? (
        <div style={{ padding: 20, fontSize: 13, color: 'var(--sk-fg-muted)' }}>
          در حال بارگذاری…
        </div>
      ) : detail.status === 'error' ? (
        <div style={{ padding: 20, fontSize: 13, color: 'var(--sk-fg-muted)' }}>
          {detail.error.status === 404 || detail.error.status === 403
            ? 'این سرنخ پیدا نشد یا به آن دسترسی ندارید.'
            : 'بارگذاری سرنخ انجام نشد.'}
        </div>
      ) : lead ? (
        <>
          <div
            style={{
              padding: '16px 20px',
              display: 'flex',
              gap: 14,
              alignItems: 'flex-start',
              borderBottom: '1px solid var(--sk-divider)',
            }}
          >
            <span className="sk-avatar" style={{ width: 48, height: 48, flex: '0 0 auto' }}>
              {initialsOf(lead.title)}
            </span>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: 'var(--sk-fg-muted)' }}>
                  {lead.leadKindLabel ?? 'سرنخ'}
                  {lead.leadKind === 'organization' && (lead.personFirstName || lead.personLastName)
                    ? ` · رابط: ${[lead.personFirstName, lead.personLastName].filter(Boolean).join(' ')}`
                    : ''}
                </span>
                <V2Badge tone={LEAD_STATUS_TONE[lead.status]}>{lead.statusLabel}</V2Badge>
              </div>
              <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                مسئول: {lead.owner?.name ?? 'بدون مسئول'} · ثبت{' '}
                {relativeWhen(lead.createdAt, { withTime: false })}
              </span>
              <LabelChips labels={lead.labels} />
              <span style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }} dir="ltr">
                {lead.ref}
              </span>
            </div>
          </div>

          {result ? (
            <div
              role="status"
              style={{
                margin: '12px 20px 0',
                padding: '10px 12px',
                borderRadius: 'var(--sk-r-md)',
                background: 'var(--sk-success-soft)',
                fontSize: 13,
              }}
            >
              {result}
            </div>
          ) : null}
          {error ? (
            <div
              role="alert"
              style={{
                margin: '12px 20px 0',
                padding: '10px 12px',
                borderRadius: 'var(--sk-r-md)',
                background: 'var(--sk-danger-soft)',
                fontSize: 13,
              }}
            >
              {error}
            </div>
          ) : null}

          <div style={{ padding: '6px 20px 14px' }}>
            <Row label="شمارهٔ همراه">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span dir="ltr" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {revealed?.mobile
                    ? faDigits(revealed.mobile)
                    : lead.contact.mobileMasked
                      ? formatMasked(lead.contact.mobileMasked)
                      : '—'}
                </span>
                {lead.contact.hasContact && !revealed ? (
                  <button
                    type="button"
                    className="sk-btn sk-btn--ghost sk-btn--sm"
                    disabled={revealing}
                    onClick={() => void reveal()}
                    title="نمایش کامل در سابقهٔ دسترسی ثبت می‌شود"
                  >
                    نمایش کامل
                  </button>
                ) : null}
              </span>
            </Row>
            {lead.contact.phoneMasked || revealed?.phone ? (
              <Row label="تلفن ثابت">
                <span dir="ltr">
                  {revealed?.phone
                    ? faDigits(revealed.phone)
                    : formatMasked(lead.contact.phoneMasked ?? '')}
                </span>
              </Row>
            ) : null}
            {lead.contact.altContactLabel ? (
              <Row label={lead.contact.altContactLabel}>
                <span dir="ltr">{revealed?.altContactValue ?? '···'}</span>
              </Row>
            ) : null}
            <Row label="منبع">
              <span className="sk-tag">{lead.sourceLabel || '—'}</span>
              {lead.sourceDetail ? (
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginInlineStart: 6 }}>
                  {lead.sourceDetail}
                </span>
              ) : null}
            </Row>
            <Row label="موضوع نیاز">{lead.needSubject ?? '—'}</Row>
            <Row label="آخرین فعالیت">
              {lead.lastActivity ? (
                <span title={exactWhen(lead.lastActivity.at)}>
                  {lead.lastActivity.title} — {relativeWhen(lead.lastActivity.at)}
                </span>
              ) : (
                <span style={{ color: 'var(--sk-fg-subtle)' }}>هنوز تماسی ثبت نشده</span>
              )}
            </Row>
            <Row label="اقدام بعدی">
              {next ? (
                <span
                  title={next.dueAt ? exactWhen(next.dueAt) : undefined}
                  style={{ color: tone === 'overdue' ? 'var(--sk-danger)' : undefined }}
                >
                  {next.title}
                  {next.dueAt ? ` — ${relativeWhen(next.dueAt)}` : ''}
                  {tone === 'overdue' ? ' (عقب‌افتاده)' : ''}
                </span>
              ) : (
                <span style={{ color: 'var(--sk-warn)' }}>بدون اقدام تعیین‌شده</span>
              )}
            </Row>
            {lead.disqualifyReason && !lead.deal.lostReason ? (
              <Row label="دلیل رد">{lead.disqualifyReason}</Row>
            ) : null}
          </div>

          <div className="sk-card-header" style={{ borderTop: '1px solid var(--sk-divider)' }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>معامله</span>
          </div>
          <div style={{ padding: '0 20px 14px' }}>
            <Row label="مشتری">
              {lead.deal.customer || lead.deal.wonCustomerRef ? (
                <Link
                  to={`${base}/crm/customers/${encodeURIComponent(lead.deal.wonCustomerRef ?? lead.deal.customer?.ref ?? '')}`}
                  style={{ color: 'var(--sk-brand-600)' }}
                >
                  {lead.deal.customer?.label ?? lead.deal.wonCustomerRef}
                </Link>
              ) : (
                <span style={{ color: 'var(--sk-fg-subtle)' }}>هنوز مشتری نشده</span>
              )}
            </Row>
            <Row label="مرحله">
              {lead.deal.stage ? (
                <span>
                  {lead.deal.stage.name} · احتمال {faDigits(lead.deal.probabilityPct)}٪
                </span>
              ) : (
                '—'
              )}
            </Row>
            <Row label={`مبلغ برآوردی (${unitLabel})`}>
              <b style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatRialFa(lead.deal.amountRial, unit)}
              </b>
              {lead.status !== 'converted' && lead.status !== 'disqualified' ? (
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginInlineStart: 8 }}>
                  موزون: {formatRialFa(lead.deal.weightedAmountRial, unit)}
                </span>
              ) : null}
            </Row>
            <Row label="تاریخ احتمالی بستن">
              {lead.deal.expectedCloseDate ? displayJalaliDate(lead.deal.expectedCloseDate) : '—'}
            </Row>
            <Row label="اولویت">
              <V2Badge tone={LEAD_PRIORITY_TONE[lead.deal.priority]}>
                {lead.deal.priorityLabel}
              </V2Badge>
            </Row>
            {lead.deal.needDescription ? (
              <Row label="شرح نیاز">{lead.deal.needDescription}</Row>
            ) : null}
            <Row label="محصولات مورد نظر">
              {lead.products.length > 0 ? (
                <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
                  {lead.products.map((product) => (
                    <span key={product.ref} className="sk-tag">
                      {product.name}
                    </span>
                  ))}
                </span>
              ) : (
                <span style={{ color: 'var(--sk-fg-subtle)' }}>—</span>
              )}
            </Row>
            {lead.deal.lostReason ? (
              <Row label="دلیل از دست رفتن">
                {lead.deal.lostReason.label}
                {lead.deal.lostReason.note ? ` — ${lead.deal.lostReason.note}` : ''}
              </Row>
            ) : null}
            {lead.deal.closedAt ? (
              <Row label={lead.status === 'converted' ? 'تاریخ برنده شدن' : 'تاریخ از دست رفتن'}>
                <span title={exactWhen(lead.deal.closedAt)}>
                  {relativeWhen(lead.deal.closedAt)}
                </span>
              </Row>
            ) : null}
          </div>

          {mode === 'stage' ? (
            <div style={actionBox}>
              <strong style={{ fontSize: 14 }}>انتقال به مرحلهٔ بعد</strong>
              <span style={hintStyle}>
                سرنخ فقط به مرحله‌ای بعد از مرحلهٔ فعلی می‌رود؛ هر تغییر در سابقهٔ مراحل ثبت می‌شود.
              </span>
              <label style={labelStyle} htmlFor="stage-next">
                مرحلهٔ جدید
              </label>
              <select
                id="stage-next"
                className="sk-select"
                value={stageRef}
                onChange={(e) => setStageRef(e.target.value)}
              >
                {lead.allowedStages.map((st) => (
                  <option key={st.ref} value={st.ref}>
                    {st.name} · احتمال {faDigits(st.probabilityPct)}٪
                  </option>
                ))}
              </select>
              {fieldErrors['stageRef'] ? (
                <span role="alert" style={fieldErrorStyle}>
                  {fieldErrors['stageRef']}
                </span>
              ) : null}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="sk-btn sk-btn--primary"
                  disabled={busy || !stageRef}
                  onClick={() => void moveStage()}
                >
                  {busy ? 'در حال ثبت…' : 'انتقال'}
                </button>
                <button
                  type="button"
                  className="sk-btn sk-btn--ghost"
                  onClick={() => setMode('view')}
                >
                  انصراف
                </button>
              </div>
            </div>
          ) : null}

          {mode === 'win' ? (
            <div style={actionBox}>
              <strong style={{ fontSize: 14 }}>برنده شد</strong>
              <span style={hintStyle}>
                سرنخ به مشتری تبدیل می‌شود و از قیف فروش بیرون می‌رود. پیش‌فاکتور و سفارش را بعد از
                آن از همین مشتری ادامه می‌دهید.
              </span>
              {lead.deal.customer ? (
                <span style={{ fontSize: 13 }}>
                  مشتری:{' '}
                  <b>
                    <PartyLink base={base} partyRef={lead.deal.customer.ref}>
                      {lead.deal.customer.label}
                    </PartyLink>
                  </b>{' '}
                  (معامله برای مشتری موجود ثبت شده است)
                </span>
              ) : (
                <fieldset style={{ border: 0, margin: 0, padding: 0, display: 'grid', gap: 8 }}>
                  <legend style={labelStyle}>این سرنخ کدام مشتری شود؟</legend>
                  {lead.actions.canCreateCustomer ? (
                    <label style={radioRow}>
                      <input
                        type="radio"
                        name="win-choice"
                        checked={winChoice === 'create' || winChoice === 'default'}
                        onChange={() => setWinChoice('create')}
                      />
                      مشتری جدید از اطلاعات همین سرنخ ساخته شود
                    </label>
                  ) : null}
                  <label style={radioRow}>
                    <input
                      type="radio"
                      name="win-choice"
                      checked={winChoice === 'existing'}
                      onChange={() => setWinChoice('existing')}
                    />
                    به یکی از مشتریان موجود وصل شود
                  </label>
                  {winChoice === 'existing' ? (
                    <select
                      className="sk-select"
                      aria-label="مشتری"
                      value={winPartyRef}
                      disabled={customers.status !== 'ready'}
                      onChange={(e) => setWinPartyRef(e.target.value)}
                    >
                      <option value="">
                        {customers.status === 'loading'
                          ? 'در حال دریافت مشتریان…'
                          : '— انتخاب مشتری —'}
                      </option>
                      {customers.status === 'ready'
                        ? customers.data.items.map((party) => (
                            <option key={String(party['code'])} value={String(party['code'])}>
                              {String(party['name'] ?? party['code'])}
                            </option>
                          ))
                        : null}
                    </select>
                  ) : null}
                  {fieldErrors['partyRef'] ? (
                    <span role="alert" style={fieldErrorStyle}>
                      {fieldErrors['partyRef']}
                    </span>
                  ) : null}
                </fieldset>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="sk-btn sk-btn--primary"
                  disabled={
                    busy ||
                    (!lead.deal.customer &&
                      ((winChoice === 'existing' && !winPartyRef) ||
                        (winChoice !== 'existing' && !lead.actions.canCreateCustomer)))
                  }
                  onClick={() => void win()}
                >
                  {busy ? 'در حال ثبت…' : 'تأیید برنده شدن'}
                </button>
                <button
                  type="button"
                  className="sk-btn sk-btn--ghost"
                  onClick={() => setMode('view')}
                >
                  انصراف
                </button>
              </div>
            </div>
          ) : null}

          {mode === 'call' ? (
            <div style={actionBox}>
              <strong style={{ fontSize: 14 }}>ثبت تماس انجام‌شده</strong>
              <span style={hintStyle}>
                ثبت دستی تماسی که انجام شده است؛ به معنای تماس برقرارشده از تلفن ابری نیست.
              </span>
              <label style={labelStyle} htmlFor="call-outcome">
                نتیجه
              </label>
              <select
                id="call-outcome"
                className="sk-select"
                value={callOutcome}
                onChange={(e) => setCallOutcome(e.target.value)}
              >
                {CALL_OUTCOMES.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
              <label style={labelStyle} htmlFor="call-summary">
                خلاصه
              </label>
              <textarea
                id="call-summary"
                className="sk-textarea"
                rows={2}
                value={callSummary}
                onChange={(e) => setCallSummary(e.target.value)}
              />
              <span style={labelStyle}>پیگیری بعدی (اختیاری)</span>
              <JalaliDateTimeInput value={callNext} onChange={setCallNext} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="sk-btn sk-btn--primary"
                  disabled={busy}
                  onClick={() => void logCall()}
                >
                  {busy ? 'در حال ثبت…' : 'ثبت تماس'}
                </button>
                <button
                  type="button"
                  className="sk-btn sk-btn--ghost"
                  onClick={() => setMode('view')}
                >
                  انصراف
                </button>
              </div>
            </div>
          ) : null}

          {mode === 'lose' ? (
            <div style={actionBox}>
              <strong style={{ fontSize: 14 }}>از دست رفت</strong>
              <span style={hintStyle}>
                دلیل از دست رفتن در گزارش‌ها می‌آید و بعداً قابل تغییر نیست.
              </span>
              <label style={labelStyle} htmlFor="lost-reason">
                دلیل <span style={{ color: 'var(--sk-danger)' }}>*</span>
              </label>
              <select
                id="lost-reason"
                className="sk-select"
                value={lostReasonCode}
                onChange={(e) => setLostReasonCode(e.target.value)}
              >
                <option value="">— انتخاب دلیل —</option>
                {lead.lostReasons.map((reasonOption) => (
                  <option key={reasonOption.ref} value={reasonOption.ref}>
                    {reasonOption.name}
                  </option>
                ))}
              </select>
              {fieldErrors['lostReasonCode'] ? (
                <span role="alert" style={fieldErrorStyle}>
                  {fieldErrors['lostReasonCode']}
                </span>
              ) : null}
              <label style={labelStyle} htmlFor="lost-note">
                توضیح{lostReasonCode === 'other' ? ' *' : ' (اختیاری)'}
              </label>
              <textarea
                id="lost-note"
                className="sk-textarea"
                rows={2}
                value={lostNote}
                onChange={(e) => setLostNote(e.target.value)}
              />
              {fieldErrors['note'] ? (
                <span role="alert" style={fieldErrorStyle}>
                  {fieldErrors['note']}
                </span>
              ) : null}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="sk-btn sk-btn--danger"
                  disabled={busy || !lostReasonCode}
                  onClick={() => void lose()}
                >
                  {busy ? 'در حال ثبت…' : 'ثبت از دست رفتن'}
                </button>
                <button
                  type="button"
                  className="sk-btn sk-btn--ghost"
                  onClick={() => setMode('view')}
                >
                  انصراف
                </button>
              </div>
            </div>
          ) : null}

          <div className="sk-card-header" style={{ borderTop: '1px solid var(--sk-divider)' }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>سابقهٔ فعالیت</span>
          </div>
          {lead.timeline.length === 0 ? (
            <div style={{ padding: '12px 20px', fontSize: 13, color: 'var(--sk-fg-subtle)' }}>
              هنوز فعالیت یا یادداشتی ثبت نشده است.
            </div>
          ) : (
            <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {lead.timeline.map((entry) => (
                <li
                  key={entry.ref}
                  style={{
                    display: 'flex',
                    gap: 12,
                    padding: '12px 20px',
                    borderBottom: '1px solid var(--sk-divider)',
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      marginTop: 6,
                      flex: '0 0 auto',
                      background:
                        entry.kind === 'note' ? 'var(--sk-border-strong)' : 'var(--sk-brand-600)',
                    }}
                  />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span style={{ fontSize: 13, color: 'var(--sk-fg)' }}>
                      {entry.typeLabel} — {entry.title}
                      {entry.statusLabel && entry.kind === 'activity' ? (
                        <span style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}>
                          {' '}
                          ({entry.statusLabel})
                        </span>
                      ) : null}
                    </span>
                    <span
                      style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}
                      title={exactWhen(entry.at)}
                    >
                      {entry.by ?? '—'} · {relativeWhen(entry.at)}
                      {entry.dueAt ? ` · موعد ${relativeWhen(entry.dueAt)}` : ''}
                    </span>
                  </div>
                </li>
              ))}
            </ol>
          )}

          <div className="sk-card-header" style={{ borderTop: '1px solid var(--sk-divider)' }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>سابقهٔ مراحل</span>
          </div>
          {lead.stageTimeline.length === 0 ? (
            <div style={{ padding: '12px 20px', fontSize: 13, color: 'var(--sk-fg-subtle)' }}>
              هنوز تغییر مرحله‌ای ثبت نشده است.
            </div>
          ) : (
            <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {lead.stageTimeline.map((entry, index) => (
                <li
                  key={`${entry.transitionedAt}-${index}`}
                  style={{
                    padding: '10px 20px',
                    borderBottom: '1px solid var(--sk-divider)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    fontSize: 13,
                  }}
                >
                  <span>
                    {entry.previousStageName ? `${entry.previousStageName} ← ` : ''}
                    <b>{entry.nextStageName ?? '—'}</b>
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}>
                    {entry.actor?.name ?? '—'} · {relativeWhen(entry.transitionedAt)}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </>
      ) : null}
    </SidePanel>
  );
}

const actionBox: React.CSSProperties = {
  margin: '0 20px 16px',
  padding: 14,
  borderRadius: 'var(--sk-r-md)',
  border: '1px solid var(--sk-border)',
  background: 'var(--sk-bg-canvas)',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};
const hintStyle: React.CSSProperties = {
  fontSize: 12,
  lineHeight: 1.7,
  color: 'var(--sk-fg-muted)',
};
const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 500, color: 'var(--sk-fg)' };
const fieldErrorStyle: React.CSSProperties = { fontSize: 12, color: 'var(--sk-danger)' };
const radioRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 13,
  cursor: 'pointer',
};
